import React, { useState } from "react";
import axios from "axios";
import { companyInfo } from "../mock";
import { Phone, Mail, MapPin, Instagram, Facebook, Twitter, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    movingFrom: "",
    movingTo: "",
    message: "",
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.phone) {
      toast({ title: "Please fill in required fields", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/quotes`,
        {
          full_name: form.fullName,
          email: form.email,
          phone: form.phone,
          moving_from: form.movingFrom,
          moving_to: form.movingTo,
          message: form.message,
        },
        { withCredentials: true }
      );
      toast({ title: "Thank you!", description: "Our team will contact you shortly." });
      setForm({ fullName: "", email: "", phone: "", movingFrom: "", movingTo: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-4xl md:text-5xl font-bold">
            CONTACT <span className="text-[color:var(--brand-red)]">US</span>
          </h2>
          <div className="h-1 w-16 bg-[color:var(--brand-red)] mt-4 mb-6" />
          <p className="text-gray-700 leading-relaxed max-w-lg">
            At <span className="font-semibold">ERM CARGO & PACKAGING L.L.C.</span> (ERM Relocations & Storages), we&apos;re always ready to assist with your relocation, storage, and cargo needs. Contact our team today for quick support, expert guidance, or a personalized quote.
          </p>

          <div className="mt-8 space-y-4">
            <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 text-gray-800 hover:text-[color:var(--brand-red)] transition-colors">
              <div className="w-10 h-10 rounded-full bg-[color:var(--brand-red)]/10 flex items-center justify-center">
                <Phone size={18} className="text-[color:var(--brand-red)]" />
              </div>
              <span className="font-medium">{companyInfo.phone}</span>
            </a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 text-gray-800 hover:text-[color:var(--brand-red)] transition-colors">
              <div className="w-10 h-10 rounded-full bg-[color:var(--brand-red)]/10 flex items-center justify-center">
                <Mail size={18} className="text-[color:var(--brand-red)]" />
              </div>
              <span className="font-medium">{companyInfo.email}</span>
            </a>
            <div className="flex items-center gap-3 text-gray-800">
              <div className="w-10 h-10 rounded-full bg-[color:var(--brand-red)]/10 flex items-center justify-center">
                <MapPin size={18} className="text-[color:var(--brand-red)]" />
              </div>
              <span className="font-medium">{companyInfo.address}</span>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <SocialLink icon={Instagram} href="#" />
            <SocialLink icon={Facebook} href="#" />
            <SocialLink icon={Twitter} href="#" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-100 rounded-md p-6 md:p-8 shadow-sm">
          <div className="grid grid-cols-1 gap-4">
            <Field label="Full Name" name="fullName" required value={form.fullName} onChange={handleChange} />
            <Field label="Email" name="email" type="email" required value={form.email} onChange={handleChange} />
            <Field label="Phone" name="phone" required value={form.phone} onChange={handleChange} />
            <Field label="Moving From" name="movingFrom" value={form.movingFrom} onChange={handleChange} />
            <Field label="Moving To" name="movingTo" value={form.movingTo} onChange={handleChange} />
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-600">Message</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                className="mt-1 w-full border-b-2 border-gray-300 bg-transparent focus:border-[color:var(--brand-red)] focus:outline-none py-2 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-red font-display uppercase tracking-wider px-8 py-3 rounded mt-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Sending..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", required, value, onChange }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-gray-600">
      {label}{required && <span className="text-[color:var(--brand-red)]">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="mt-1 w-full border-b-2 border-gray-300 bg-transparent focus:border-[color:var(--brand-red)] focus:outline-none py-2"
    />
  </div>
);

const SocialLink = ({ icon: Icon, href }) => (
  <a
    href={href}
    className="w-10 h-10 rounded-full bg-[color:var(--brand-teal)] text-white flex items-center justify-center hover:bg-[color:var(--brand-red)] transition-colors"
  >
    <Icon size={18} />
  </a>
);

export default Contact;
